import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiGet, apiPost, usePurchaseApi } from './purchaseApi';
import { KycStartForm, type KycBlock } from './KycStartForm';
import { LifeHandoffPanel } from './LifeHandoffPanel';
// Only the error PARSERS -- never the api instance, which would attach a
// bearer token this unauthenticated page must not send.
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import { formatCurrency } from '@/utils';
import type {
  AfterAcceptanceContext,
  PublicQuoteData,
  PublicQuoteDocument,
} from './PublicQuote';

/**
 * The one purchase journey after the customer accepts an option:
 * "Your details" -> "Proceed to payment" -> whatever the insurer needs next.
 *
 * Every insurer goes through the SAME button. The server
 * (`PurchaseService.proceed`, `POST /api/public/quotes/<token>/proceed/`)
 * decides the next step and this page only acts on `next_step`:
 *
 *   pay           -> go to the insurer's payment page (`redirect_url`)
 *   kyc           -> "Complete your KYC with <insurer>", then re-check on return
 *   kyc_start     -> the shared KYC step: consent (and PAN / date of birth if none on file)
 *   wait          -> say why, and check again
 *   details       -> say what is missing, and reopen the form
 *   offline       -> "Your advisor will send you a payment link"
 *   done          -> policy issued (number + PDF when attached)
 *   confirm_price -> the insurer priced it differently: show it, ask to confirm
 *
 * The details form posts to `/proposal-details/`, which stores the answers
 * where the purchase service reads them. Nothing the customer typed comes back
 * from the server -- the page is told only whether details are on file.
 */

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/bmp'];
const RECHECK_MS = 15_000;
const MAX_AUTO_RECHECKS = 6;

/** Navigation seam: tests replace `go` instead of the whole of window.location. */
export const browser = {
  go(url: string) {
    window.location.assign(url);
  },
};

type NextStep = 'pay' | 'kyc' | 'kyc_start' | 'handoff' | 'wait' | 'details' | 'offline' | 'done' | 'confirm_price';

export interface ProceedResult {
  next_step: NextStep;
  redirect_url: string;
  application_id: string;
  kyc_status: string;
  insurer_code: string;
  message: string;
  policy_number?: string;
  insurer_premium?: string;
  quoted_premium?: string;
  /** #571: the shared KYC step's state, present on `kyc_start` (and `kyc` / `wait` / `details` from it). */
  kyc?: KycBlock;
}

interface StateOption {
  name: string;
  code: string;
}

interface DetailsMeta {
  states: StateOption[];
  prefill: DetailsPrefill;
  vehicle_numbers_on_file: boolean;
}

/**
 * What the form starts with. The chat's details form (`/m/form/:token`) adds the
 * vehicle registry's record: the previous policy, and engine / chassis numbers
 * the server sends ONLY masked (`•••••4521`). Sent back untouched, the server
 * uses the number it holds; typed over, the new number is used. The public
 * quote page's endpoint never sends these, so its form is unchanged.
 */
interface DetailsPrefill {
  first_name: string;
  last_name: string;
  previous_insurer?: string;
  previous_policy_number?: string;
  engine_number?: string;
  chassis_number?: string;
}

const PREFILLED_FIELDS = ['previous_insurer', 'previous_policy_number', 'engine_number', 'chassis_number'] as const;
const MASK_CHAR = '•';

type KycMethod = 'pan' | 'ckyc' | 'documents';

const ID_DOCS = [
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'driving_licence', label: 'Driving licence' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'passport', label: 'Passport' },
];

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';
const primaryButton =
  'px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButton =
  'px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

/** Drawn only where the server says the purchase journey applies. */
export function purchaseAfterAcceptance(ctx: AfterAcceptanceContext) {
  // Null purchase: the legacy page. Completed: the page's own issued-policy
  // section says everything; nothing to buy.
  if (!ctx.quote.purchase || ctx.quote.status === 'completed') return null;
  return <PurchasePanel ctx={ctx} />;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {message}
    </p>
  );
}

// ---------------------------------------------------------------------------
// "Your details"
// ---------------------------------------------------------------------------

interface DetailsFormProps {
  token: string;
  brokerName: string;
  onSaved: (data: PublicQuoteData) => void;
  onCancel?: () => void;
  /** Shown above the form: what the insurer said is missing, say. */
  notice?: string;
}

export function DetailsForm({ token, brokerName, onSaved, onCancel, notice }: DetailsFormProps) {
  const api = usePurchaseApi();
  const [meta, setMeta] = useState<DetailsMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    mobile: '',
    email: '',
    flat_number: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    pan_number: '',
    ckyc_number: '',
    id_doc_type: 'aadhaar',
    engine_number: '',
    chassis_number: '',
    previous_insurer: '',
    previous_policy_number: '',
  });
  const [kycMethod, setKycMethod] = useState<KycMethod>('pan');
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [whatsapp, setWhatsapp] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet(api, `${api.base(token)}/proposal-details/`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data as DetailsMeta;
        setMeta(data);
        setValues((v) => {
          const next: Record<string, string> = {
            ...v,
            first_name: v.first_name || data.prefill?.first_name || '',
            last_name: v.last_name || data.prefill?.last_name || '',
          };
          for (const field of PREFILLED_FIELDS) next[field] = v[field] || data.prefill?.[field] || '';
          return next;
        });
      })
      .catch((err) => {
        if (!cancelled) setMetaError(getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token, api]);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const pickFile = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      if (file && !IMAGE_TYPES.includes(file.type)) next[field] = 'Upload a JPG or BMP image.';
      else if (file && file.size > MAX_IMAGE_BYTES) next[field] = 'This image is larger than 2 MB.';
      return next;
    });
    setFiles((f) => ({ ...f, [field]: file }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const localErrors: Record<string, string> = {};
    if (kycMethod === 'documents') {
      for (const field of ['id_doc_front', 'id_doc_back', 'photo']) {
        const file = files[field];
        if (!file) continue;
        if (!IMAGE_TYPES.includes(file.type)) localErrors[field] = 'Upload a JPG or BMP image.';
        else if (file.size > MAX_IMAGE_BYTES) localErrors[field] = 'This image is larger than 2 MB.';
      }
    }
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      const body: Record<string, unknown> = {
        first_name: values.first_name,
        last_name: values.last_name,
        date_of_birth: values.date_of_birth,
        gender: values.gender,
        mobile: values.mobile,
        email: values.email,
        flat_number: values.flat_number,
        street: values.street,
        city: values.city,
        state: values.state,
        pincode: values.pincode,
        kyc_method: kycMethod,
        engine_number: values.engine_number,
        chassis_number: values.chassis_number,
        whatsapp_consent: whatsapp,
      };
      // Only the previous-policy answers this form offered (the chat's registry record).
      if (meta?.prefill?.previous_insurer) body.previous_insurer = values.previous_insurer;
      if (meta?.prefill?.previous_policy_number) body.previous_policy_number = values.previous_policy_number;
      if (kycMethod === 'pan') body.pan_number = values.pan_number;
      if (kycMethod === 'ckyc') body.ckyc_number = values.ckyc_number;
      if (kycMethod === 'documents') {
        body.id_doc_type = values.id_doc_type;
        for (const field of ['id_doc_front', 'id_doc_back', 'photo']) {
          const file = files[field];
          if (file) body[field] = await readAsDataUrl(file);
        }
      }
      const res = await apiPost(api, `${api.base(token)}/proposal-details/`, body);
      onSaved(res.data as PublicQuoteData);
    } catch (err) {
      const fieldErrors = getFieldErrors(err);
      const nonField = fieldErrors[NON_FIELD_KEY] || fieldErrors.detail;
      setErrors(fieldErrors);
      if (nonField) setFormError(nonField);
      else if (Object.keys(fieldErrors).length === 0) setFormError(getErrorMessage(err));
      else setFormError('Please check the highlighted details.');
    } finally {
      setSaving(false);
    }
  };

  if (metaError) {
    return (
      <p className="text-sm text-red-600" role="alert">
        We could not open the details form just now. {metaError}
      </p>
    );
  }
  if (!meta) {
    return <p className="text-sm text-gray-500">Loading the details form…</p>;
  }

  const text = (field: string, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div>
      <label htmlFor={`pd-${field}`} className={labelClass}>
        {label}
      </label>
      <input
        id={`pd-${field}`}
        name={field}
        value={values[field] ?? ''}
        onChange={set(field)}
        className={inputClass}
        {...props}
      />
      <FieldError message={errors[field]} />
    </div>
  );

  const maskedNumbers = Boolean(meta.prefill?.engine_number || meta.prefill?.chassis_number);
  // A masked value is replaced wholesale, never edited around its dots.
  const selectMasked = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.includes(MASK_CHAR)) e.currentTarget.select();
  };

  const fileInput = (field: string, label: string) => (
    <div>
      <label htmlFor={`pd-${field}`} className={labelClass}>
        {label}
      </label>
      <input
        id={`pd-${field}`}
        name={field}
        type="file"
        accept="image/jpeg,image/bmp"
        onChange={pickFile(field)}
        className="w-full text-xs text-gray-600 dark:text-gray-300"
      />
      <FieldError message={errors[field]} />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-5" aria-label="Your details" noValidate>
      {notice && (
        <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200" role="status">
          {notice}
        </p>
      )}

      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">About you</legend>
        {text('first_name', 'First name', { autoComplete: 'given-name' })}
        {text('last_name', 'Last name', { autoComplete: 'family-name' })}
        {text('date_of_birth', 'Date of birth', { type: 'date', max: new Date().toISOString().slice(0, 10) })}
        <div>
          <label htmlFor="pd-gender" className={labelClass}>
            Gender
          </label>
          <select id="pd-gender" name="gender" value={values.gender} onChange={set('gender')} className={inputClass}>
            <option value="">Select</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
          <FieldError message={errors.gender} />
        </div>
        {text('mobile', 'Mobile number', { type: 'tel', inputMode: 'numeric', autoComplete: 'tel', placeholder: '10 digits' })}
        {text('email', 'Email', { type: 'email', autoComplete: 'email' })}
      </fieldset>

      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Address</legend>
        {text('flat_number', 'Flat / house', { autoComplete: 'address-line1' })}
        {text('street', 'Street / area', { autoComplete: 'address-line2' })}
        {text('city', 'City', { autoComplete: 'address-level2' })}
        <div>
          <label htmlFor="pd-state" className={labelClass}>
            State
          </label>
          <select id="pd-state" name="state" value={values.state} onChange={set('state')} className={inputClass}>
            <option value="">Select your state</option>
            {meta.states.map((s) => (
              <option key={s.code} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
          <FieldError message={errors.state} />
        </div>
        {text('pincode', 'Pincode', { inputMode: 'numeric', maxLength: 6, autoComplete: 'postal-code' })}
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Identity (KYC)</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How to verify your identity">
          {(
            [
              ['pan', 'PAN'],
              ['ckyc', 'CKYC number'],
              ['documents', 'Upload documents'],
            ] as [KycMethod, string][]
          ).map(([value, label]) => (
            <label
              key={value}
              className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm ${
                kycMethod === value
                  ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                  : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              <input
                type="radio"
                name="kyc_method"
                value={value}
                checked={kycMethod === value}
                onChange={() => setKycMethod(value)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>
        <FieldError message={errors.kyc_method} />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {kycMethod === 'pan' && text('pan_number', 'PAN', { placeholder: 'ABCDE1234F', autoCapitalize: 'characters' })}
          {kycMethod === 'ckyc' && text('ckyc_number', 'CKYC number', { inputMode: 'numeric', placeholder: '14 digits' })}
          {kycMethod === 'documents' && (
            <>
              <div>
                <label htmlFor="pd-id_doc_type" className={labelClass}>
                  Document
                </label>
                <select
                  id="pd-id_doc_type"
                  name="id_doc_type"
                  value={values.id_doc_type}
                  onChange={set('id_doc_type')}
                  className={inputClass}
                >
                  {ID_DOCS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <FieldError message={errors.id_doc_type} />
              </div>
              {fileInput('id_doc_front', 'Front (JPG/BMP, up to 2 MB)')}
              {fileInput('id_doc_back', 'Back (if it has one)')}
              {fileInput('photo', 'Passport-size photo')}
            </>
          )}
        </div>
      </fieldset>

      {(meta.prefill?.previous_insurer || meta.prefill?.previous_policy_number) && (
        <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Your previous policy</legend>
          {meta.prefill.previous_insurer && text('previous_insurer', 'Previous insurer')}
          {meta.prefill.previous_policy_number &&
            text('previous_policy_number', 'Previous policy number', { autoCapitalize: 'characters' })}
        </fieldset>
      )}

      {meta.vehicle_numbers_on_file && !maskedNumbers ? (
        <p className="text-xs text-gray-500">Your vehicle's engine and chassis numbers are already on file.</p>
      ) : (
        <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Vehicle (from your RC)</legend>
          {maskedNumbers && (
            <p className="sm:col-span-2 text-xs text-gray-500">
              From your vehicle's registration record, with only the last 4 characters shown. Leave a number as it is,
              or type the full number to change it.
            </p>
          )}
          {text('engine_number', 'Engine number', { autoCapitalize: 'characters', onFocus: selectMasked })}
          {text('chassis_number', 'Chassis number', { autoCapitalize: 'characters', onFocus: selectMasked })}
        </fieldset>
      )}

      <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={whatsapp}
          onChange={(e) => setWhatsapp(e.target.checked)}
          className="mt-0.5"
        />
        <span>Send me updates about this policy on WhatsApp.</span>
      </label>

      {formError && (
        <p className="text-sm text-red-600" role="alert">
          {formError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={saving} className={primaryButton}>
          {saving ? 'Saving…' : 'Save my details'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className={secondaryButton}>
            Cancel
          </button>
        )}
        <p className="text-xs text-gray-500">Shared only with {brokerName} and the insurer.</p>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

function PolicyDocuments({ documents, token }: { documents: PublicQuoteDocument[]; token: string }) {
  const api = usePurchaseApi();
  if (documents.length === 0) {
    return (
      <p className="mt-2 text-sm text-green-800 dark:text-green-300">
        Your policy document will appear on this page as soon as the insurer sends it.
      </p>
    );
  }
  return (
    <ul className="mt-2 space-y-1">
      {documents.map((doc) => (
        <li key={doc.id}>
          <a
            href={`${api.base(token)}/documents/${doc.id}/download/?disposition=inline`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {doc.name} (PDF)
          </a>
        </li>
      ))}
    </ul>
  );
}

export function PurchasePanel({ ctx }: { ctx: AfterAcceptanceContext }) {
  if (ctx.quote.purchase?.handoff === true) return <LifeHandoffPanel ctx={ctx} />;
  if (ctx.quote.purchase?.online !== true) {
    // Health, travel...: no insurer proposal/payment path yet. Never a
    // motor-shaped form -- the plain offline answer.
    return (
      <section aria-label="Buy this policy" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 sm:p-6">
        <p role="status" className="text-sm text-gray-800 dark:text-gray-200">
          Your advisor will send you a payment link.
        </p>
      </section>
    );
  }
  return <OnlinePurchasePanel ctx={ctx} />;
}

function OnlinePurchasePanel({ ctx }: { ctx: AfterAcceptanceContext }) {
  const { quote, token, acceptedOption, replaceQuote, reload } = ctx;
  const api = usePurchaseApi();
  const purchase = quote.purchase!;
  const details = purchase.details!;
  const insurerName = acceptedOption.insurer_name;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentReturn = params.get('payment');
  const kycReturn = params.get('kyc');

  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | undefined>();
  const [result, setResult] = useState<ProceedResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(paymentReturn === 'received' && details.submitted);
  const rechecks = useRef(0);
  const lastStep = useRef<NextStep | null>(null);

  const awaitingApproval =
    purchase.payment_gate === 'after_approval' && quote.status === 'kyc_pending_approval';
  const rejected = quote.status === 'kyc_rejected';

  const proceed = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      setBusy(true);
      setError(null);
      try {
        const res = await apiPost(api, `${api.base(token)}/proceed/`, extra);
        const answer = res.data as ProceedResult;
        lastStep.current = answer.next_step;
        setResult(answer);
        if (answer.next_step === 'pay' && answer.redirect_url) {
          if (api.openPayment) api.openPayment(answer.redirect_url);
          else browser.go(answer.redirect_url);
        } else if (answer.next_step === 'details') {
          setNotice(answer.message);
          if (details.editable) setEditing(true);
        } else if (answer.next_step === 'done') {
          void reload();
        }
        return answer;
      } catch (err) {
        setError(getErrorMessage(err));
        return null;
      } finally {
        setBusy(false);
        setConfirming(false);
      }
    },
    [token, details.editable, reload, api]
  );

  // Back from the insurer's payment page: confirm straight away.
  useEffect(() => {
    if (paymentReturn === 'received' && details.submitted) {
      void proceed();
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back from the insurer's KYC page, or back to the tab: look again.
  useEffect(() => {
    const onFocus = () => {
      if (lastStep.current === 'kyc' || lastStep.current === 'wait') {
        void proceed();
      } else if (awaitingApproval) {
        void reload();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [proceed, reload, awaitingApproval]);

  useEffect(() => {
    if (kycReturn === 'returned' && details.submitted && !awaitingApproval) {
      void proceed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "wait" re-checks itself a few times, then leaves it to the button.
  useEffect(() => {
    if (result?.next_step !== 'wait' || rechecks.current >= MAX_AUTO_RECHECKS) return;
    const handle = setTimeout(() => {
      rechecks.current += 1;
      void proceed();
    }, RECHECK_MS);
    return () => clearTimeout(handle);
  }, [result, proceed]);

  const saved = (data: PublicQuoteData) => {
    replaceQuote(data);
    setEditing(false);
    setNotice(undefined);
    setResult(null);
  };

  const showForm = (editing || !details.submitted || rejected) && details.editable;

  return (
    <section
      aria-label="Buy this policy"
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 sm:p-6 space-y-4"
    >
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {showForm ? 'Your details' : `Buy ${insurerName}`}
        </h2>
        {showForm && (
          <p className="mt-1 text-sm text-gray-500">
            {insurerName} need these to issue your policy. It takes about two minutes.
          </p>
        )}
      </div>

      {rejected && quote.kyc.rejection_reason && (
        <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
          {quote.broker_name} could not accept your documents: {quote.kyc.rejection_reason}
        </p>
      )}

      {!details.submitted && !details.editable && details.locked_reason && (
        <p className="text-sm text-gray-600 dark:text-gray-400">{details.locked_reason}</p>
      )}

      {showForm && (
        <DetailsForm
          token={token}
          brokerName={quote.broker_name}
          onSaved={saved}
          onCancel={details.submitted && !rejected ? () => setEditing(false) : undefined}
          notice={notice}
        />
      )}

      {!showForm && details.submitted && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <p className="text-green-700 dark:text-green-400">Your details are saved.</p>
            {details.editable && (
              <button type="button" onClick={() => setEditing(true)} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                Edit details
              </button>
            )}
          </div>

          {confirming && (
            <p role="status" className="text-sm text-gray-700 dark:text-gray-300">
              Confirming your payment…
            </p>
          )}
          {paymentReturn === 'cancelled' && !result && (
            <p role="status" className="text-sm text-amber-800 dark:text-amber-300">
              Your payment was not completed. You can try again below.
            </p>
          )}
          {paymentReturn === 'unconfirmed' && !result && (
            <p role="status" className="text-sm text-amber-800 dark:text-amber-300">
              We could not confirm a payment yet. If you paid, check again in a minute.
            </p>
          )}

          {awaitingApproval ? (
            <p role="status" className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-sm text-blue-900 dark:text-blue-200">
              Waiting for {quote.broker_name} to approve your documents. You can pay as soon as they have.
            </p>
          ) : (
            <Outcome
              result={result}
              busy={busy || confirming}
              insurerName={insurerName}
              quote={quote}
              token={token}
              onProceed={() => void proceed()}
              onConfirmPrice={(premium) => void proceed({ confirm_premium: premium })}
            />
          )}

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Outcome({
  result,
  busy,
  insurerName,
  quote,
  token,
  onProceed,
  onConfirmPrice,
}: {
  result: ProceedResult | null;
  busy: boolean;
  insurerName: string;
  quote: PublicQuoteData;
  token: string;
  onProceed: () => void;
  onConfirmPrice: (premium: string) => void;
}) {
  const payButton = (label = 'Proceed to payment') => (
    <button type="button" onClick={onProceed} disabled={busy} className={primaryButton}>
      {busy ? 'Please wait…' : label}
    </button>
  );

  if (!result || result.next_step === 'pay') {
    return (
      <div className="space-y-2">
        {payButton()}
        {result?.next_step === 'pay' && (
          <p className="text-xs text-gray-500">
            Taking you to {insurerName}'s secure payment page… If nothing happens,{' '}
            <a href={result.redirect_url} className="font-medium text-primary-600">
              open it here
            </a>
            .
          </p>
        )}
      </div>
    );
  }

  switch (result.next_step) {
    case 'kyc':
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">{result.message}</p>
          <a href={result.redirect_url} className={`${primaryButton} inline-block`}>
            Complete your KYC with {insurerName}
          </a>
          <p className="text-xs text-gray-500">Come back to this page when you are done -- it checks again by itself.</p>
        </div>
      );
    case 'kyc_start':
      return (
        <KycStartForm
          token={token}
          insurerName={insurerName}
          message={result.message}
          kyc={result.kyc ?? { status: 'not_started' }}
          onStarted={onProceed}
        />
      );
    case 'wait':
      return (
        <div className="space-y-2">
          <p role="status" className="text-sm text-gray-700 dark:text-gray-300">
            {result.message}
          </p>
          <button type="button" onClick={onProceed} disabled={busy} className={secondaryButton}>
            Check again
          </button>
        </div>
      );
    case 'details':
      return (
        <div className="space-y-2">
          <p className="text-sm text-amber-800 dark:text-amber-300">{result.message}</p>
          {payButton('Try again')}
        </div>
      );
    case 'offline':
      return (
        <p role="status" className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3 text-sm text-gray-800 dark:text-gray-200">
          {result.message || 'Your advisor will send you a payment link.'}
        </p>
      );
    case 'done':
      return (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <p className="text-sm font-semibold text-green-900 dark:text-green-200">Your policy has been issued.</p>
          {result.policy_number && (
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">Policy number {result.policy_number}</p>
          )}
          <PolicyDocuments documents={quote.policy_documents} token={token} />
        </div>
      );
    case 'confirm_price': {
      const premium = result.insurer_premium || '';
      return (
        <div className="space-y-2">
          <p className="text-sm text-amber-900 dark:text-amber-200">{result.message}</p>
          <button type="button" onClick={() => onConfirmPrice(premium)} disabled={busy} className={primaryButton}>
            Confirm {formatCurrency(Number(premium))} and continue
          </button>
        </div>
      );
    }
    default:
      return payButton();
  }
}
